import { getResourceThumbnail } from '~/utils/common'

export interface VideoSchemaInput {
    name: string
    description?: string
    contentUrl?: string
    embedUrl?: string
    thumbnailUrl?: string
    uploadDate?: string
    duration?: string
    mainEntityOfPage?: string
    encodingFormat?: string
}

const FALLBACK_UPLOAD_DATE = '2025-10-20T00:00:00+08:00'

function extractYouTubeVideoId(embedUrl: string): string | null {
    try {
        const url = new URL(embedUrl)
        const match = url.pathname.match(/\/embed\/([^/?]+)/)
        return match?.[1] || null
    } catch {
        return null
    }
}

function toYouTubeWatchUrl(embedUrl: string): string | undefined {
    const id = extractYouTubeVideoId(embedUrl)
    return id ? `https://www.youtube.com/watch?v=${id}` : undefined
}

function toYouTubeThumbnail(embedUrl: string): string | undefined {
    const id = extractYouTubeVideoId(embedUrl)
    return id ? `https://img.youtube.com/vi/${id}/maxresdefault.jpg` : undefined
}

function hasIsoTimezone(dateStr: string): boolean {
    return /([zZ]|[+-]\d{2}:?\d{2})$/.test(dateStr)
}

function normalizeUploadDate(dateInput?: string): string {
    if (!dateInput) return FALLBACK_UPLOAD_DATE
    const value = dateInput.trim()
    if (!value) return FALLBACK_UPLOAD_DATE
    if (hasIsoTimezone(value)) return value
    if (value.includes('T')) return `${value}+08:00`
    return `${value}T00:00:00+08:00`
}

function isVideoLikeUrl(url: string): boolean {
    return /\.(mp4|webm|mov|avi|mkv|flv|wmv|m4v|mpg|mpeg|3gp|ogv|m2ts|mts|ts|vob|rm|rmvb|asf|divx)(\?.*)?$/i.test(
        url
    )
}

function pickThumbnailUrl(input: VideoSchemaInput): string | undefined {
    const raw = input.thumbnailUrl?.trim()
    if (raw && !isVideoLikeUrl(raw)) return raw

    if (input.embedUrl) {
        const ytThumb = toYouTubeThumbnail(input.embedUrl)
        if (ytThumb) return ytThumb
    }

    const fromContent = input.contentUrl?.trim()
    if (fromContent) {
        const guessed = getResourceThumbnail(fromContent)
        if (guessed && !isVideoLikeUrl(guessed)) return guessed
    }

    return undefined
}

export function buildVideoObject(input: VideoSchemaInput): Record<string, any> | null {
    const name = input.name?.trim()
    if (!name) return null

    const description = (input.description || input.name).trim()
    const embedUrl = input.embedUrl?.trim()
    const contentUrl =
        input.contentUrl?.trim() || (embedUrl ? toYouTubeWatchUrl(embedUrl) : undefined)
    const thumbnailUrl = pickThumbnailUrl(input)

    if (!description || !thumbnailUrl || (!contentUrl && !embedUrl)) {
        return null
    }

    const videoObject: Record<string, any> = {
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        name,
        description,
        thumbnailUrl,
        uploadDate: normalizeUploadDate(input.uploadDate),
    }

    if (contentUrl) videoObject.contentUrl = contentUrl
    if (embedUrl) videoObject.embedUrl = embedUrl
    if (input.duration) videoObject.duration = input.duration
    if (input.encodingFormat) videoObject.encodingFormat = input.encodingFormat
    if (input.mainEntityOfPage) videoObject.mainEntityOfPage = input.mainEntityOfPage

    return videoObject
}
